import React from 'react';
import { Box, Container, Typography, Stack, Button } from '@mui/material';
import Grid from '@mui/material/Grid';
import StarIcon from '@mui/icons-material/Star';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CampaignIcon from '@mui/icons-material/Campaign';
import ArticleIcon from '@mui/icons-material/Article';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ShieldIcon from '@mui/icons-material/Shield';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import Header from './Header';
import SEO from './SEO';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

const parentStats = [
  { stat: '50%', label: 'of parents rank safety as their top daycare criterion', sub: '+18% increase from 32% in 2022 — growing fast', source: 'KinderCare 2025 Parent Confidence Index' },
  { stat: '47%', label: 'say caregiver vigilance is their #1 deciding factor', sub: 'More than location, cost, or curriculum', source: 'KinderCare 2025 Parent Confidence Index' },
  { stat: '75%', label: 'believe childcare workers should be government-licensed', sub: 'Like doctors, nurses, and teachers — parents expect accountability', source: 'NAEYC & MomsRising Survey' },
];

const parentQuestions = [
  {
    question: '"Do you have cameras in every room?"',
    answer: 'Yes — SMCLA covers every room simultaneously with AI-powered monitoring that never looks away.',
  },
  {
    question: '"What happens if something goes wrong with my child?"',
    answer: 'The AI flags the incident in real time. Facility management is alerted within 60 seconds. You receive a report before you would ever have been called the old way.',
  },
  {
    question: '"Who watches the footage? I don\'t want random staff watching my child."',
    answer: 'Access is strictly role-based. Only the facility owner and designated staff see flagged clips — and every access is logged with a full audit trail.',
  },
  {
    question: '"How do you screen your staff?"',
    answer: 'AI monitoring means even staff who passed background checks are held accountable in real time. The system sees patterns before any human would notice them.',
  },
  {
    question: '"How will I know if something happened?"',
    answer: 'If the facility director decides to notify you, they send a structured, professional message — not an improvised call at 6 PM. Facilities using SMCLA have the tools to communicate clearly and proactively, when they choose to.',
  },
];

const responseTimeline = [
  { icon: <NotificationsActiveIcon sx={{ fontSize: 18 }} />, time: '0–60 seconds', title: 'AI detects and flags the behavior', body: 'The system sends an immediate push notification to the facility owner and designated reviewer. No tape to rewind, no shift to wait for.' },
  { icon: <ShieldIcon sx={{ fontSize: 18 }} />, time: 'Under 5 minutes', title: 'Reviewer classifies the alert', body: 'A trained reviewer opens the full analysis: AI summary, audio transcript, visual event timeline, and sentiment score. They determine: True Alert or False Alarm.' },
  { icon: <PersonOffIcon sx={{ fontSize: 18 }} />, time: 'Under 15 minutes', title: 'Immediate action is taken', body: 'For validated incidents: the staff member is suspended, placed on leave, or formally counseled — depending on severity. No one waits until the next day.' },
  { icon: <CampaignIcon sx={{ fontSize: 18 }} />, time: 'Director\'s decision', title: 'The director chooses to notify parents', body: 'After reviewing the validated alert, the facility director decides whether and how to inform parents. They select from structured communication templates — no improvising under pressure, no inconsistent messaging.' },
  { icon: <ArticleIcon sx={{ fontSize: 18 }} />, time: 'Permanently on record', title: 'Full documentation is available', body: 'Timestamped evidence, chain-of-custody metadata, reviewer notes, and a permanent reference key — all preserved. Every notification sent is logged as part of the incident record.' },
];


const parentPhotos = [
  {
    photo: 'https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=600&q=80',
    quote: 'I switched daycares specifically because the new one showed me their monitoring system. I felt safe leaving my daughter there for the first time.',
    attribution: 'Parent of a 2-year-old, Texas',
  },
  {
    photo: 'https://images.unsplash.com/photo-1607746882042-944635dfe10e?auto=format&fit=crop&w=600&q=80',
    quote: 'When I toured, the director walked me through exactly how their AI safety system works. That level of transparency sold me before I even saw the classroom.',
    attribution: 'Parent of twins, New York',
  },
  {
    photo: 'https://images.unsplash.com/photo-1484665754804-74b091211472?auto=format&fit=crop&w=600&q=80',
    quote: 'I tell every parent I know: ask if they have AI monitoring. It\'s the difference between hoping nothing happens and knowing nothing can.',
    attribution: 'Parent of a 3-year-old, California',
  },
];


export default function ParentsPage() {
  const navigate = useNavigate();
  const scrollToContact = () => navigate('/contact');

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="For Parents — How SMCLA Protects Your Child" description="What SMCLA does to keep children safe, protect their privacy, and give parents confidence in the daycare they chose." path="/parents" />
      <Header onBookDemo={scrollToContact} />

      {/* Hero */}
      <Box sx={{ position: 'relative', py: { xs: 10, md: 14 }, px: 3, textAlign: 'center', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: '-10%', right: '-5%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.12) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 3 }}>
            <PeopleIcon sx={{ color: '#5ce1e6', fontSize: 14 }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              The Enrollment Advantage
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.2rem', md: '3.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 2.5, lineHeight: 1.1 }}>
            Parents are already choosing.<br /><Box component="span" sx={{ color: '#5ce1e6' }}>Make sure they choose you.</Box>
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 560, mx: 'auto', lineHeight: 1.65, mb: 5 }}>
            Safety is now the #1 decision driver for daycare parents. The facilities that invest in visible, verifiable safety win enrollment — and keep it.
          </Typography>
          <Button variant="contained" size="large" onClick={scrollToContact} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            Make Safety Your Competitive Edge →
          </Button>
        </Container>
      </Box>

      {/* Stats */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 6, md: 10 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            The Data Is Clear
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 6, letterSpacing: '-0.02em' }}>
            Safety is the new enrollment driver
          </Typography>
          <Stack spacing={3}>
            {parentStats.map(({ stat, label, sub, source }) => (
              <Box key={stat} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 1.5, md: 4 }, p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' }, color: '#5ce1e6', lineHeight: 1, flexShrink: 0, minWidth: { md: 120 } }}>
                  {stat}
                </Typography>
                <Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: { xs: 16, md: 18 }, color: '#f0f0f8', mb: 0.5 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 0.5 }}>
                    {sub}
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.6)' }}>
                    Source: {source}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Parent questions */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            What Parents Ask on Tour
          </Typography>
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Answer every tough question confidently
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', mb: 6, lineHeight: 1.7, maxWidth: 560 }}>
            Parents are more informed than ever. With SMCLA deployed, you have a confident, verifiable answer to every safety question they'll ask.
          </Typography>
          <Stack spacing={2.5}>
            {parentQuestions.map(({ question, answer }) => (
              <Box key={question} sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#5ce1e6', mb: 1 }}>
                  {question}
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>
                  {answer}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Enrollment advantage */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'inline-flex', p: 2, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, mb: 3 }}>
                <StarIcon sx={{ color: '#5ce1e6', fontSize: 32 }} />
              </Box>
              <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
                The enrollment advantage
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 3 }}>
                Facilities with visible, verifiable safety programs don't just retain enrollment — they attract it. Parents who feel safe become your strongest referral source.
              </Typography>
              <Stack spacing={1.5}>
                {['Higher enrollment retention — parents don\'t leave when they trust you', 'Stronger word-of-mouth referrals from confident, loyal families', 'Premium positioning — justify higher tuition with demonstrable safety standards', 'Faster enrollment recovery after any industry-wide scare'].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <CheckCircleIcon sx={{ color: '#5ce1e6', fontSize: 18, mt: 0.3, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#d0d0e0', lineHeight: 1.6 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2 }}>
                  What SMCLA gives you to say
                </Typography>
                <Stack spacing={2}>
                  {['"We use AI-powered behavioral monitoring that watches every room, every moment — even when staff can\'t."', '"Our system alerts management within 60 seconds of any suspicious behavior."', '"Every flagged clip is timestamped and tamper-evident. We protect your children and your staff."', '"We can show you exactly how it works during your tour."'].map((quote, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                      <Box sx={{ color: '#5ce1e6', fontWeight: 700, fontSize: 18, flexShrink: 0, lineHeight: 1.4 }}>›</Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontStyle: 'italic', fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>
                        {quote}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Incident Response Timeline */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ position: 'sticky', top: 100 }}>
                <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
                  When Something Is Found
                </Typography>
                <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
                  You will always know.<br />Before the day is over.
                </Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 3 }}>
                  When our AI validates an incident, a structured response process activates immediately. You are never the last to know — you are notified the same shift it happens, with full documentation.
                </Typography>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1529333166437-7750a6dd5a70?auto=format&fit=crop&w=600&q=80"
                  alt="Parent receiving a notification about their child's safety"
                  sx={{ width: '100%', borderRadius: 3, border: '1px solid rgba(255,255,255,0.06)', display: 'block' }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={0}>
                {responseTimeline.map(({ icon, time, title, body }, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.1)', border: '1px solid rgba(92,225,230,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5ce1e6' }}>
                        {icon}
                      </Box>
                      {i < responseTimeline.length - 1 && <Box sx={{ width: 1, height: 36, bgcolor: 'rgba(92,225,230,0.15)' }} />}
                    </Box>
                    <Box sx={{ pb: i < responseTimeline.length - 1 ? 4 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 12, color: '#5ce1e6' }} />
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#5ce1e6', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{time}</Typography>
                      </Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#f0f0f8', mb: 0.75 }}>{title}</Typography>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>{body}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Parent Communication feature */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
                Proactive Communication
              </Typography>
              <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
                The director decides.<br />The message is always right.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 4 }}>
                After a reviewer validates an alert, the facility director chooses whether to notify affected parents. When they do, SMCLA provides structured communication templates — professionally worded, calibrated to the situation, ready to send in seconds.
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, color: '#9499b0', lineHeight: 1.7, mb: 4 }}>
                No improvising under pressure. No inconsistent messaging. The director stays in control of what is communicated and when — while parents receive a clear, honest, reassuring message that builds confidence in the facility.
              </Typography>
              <Stack spacing={1.5}>
                {[
                  'Director sends notifications on their own timeline — never automatic',
                  'Curated templates cover every severity level',
                  'Every message sent is logged as part of the permanent incident record',
                  'Parents feel informed and protected, not alarmed',
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <CheckCircleIcon sx={{ color: '#5ce1e6', fontSize: 17, mt: 0.3, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#d0d0e0', lineHeight: 1.6 }}>{item}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ p: 3.5, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.12)', border: '1px solid rgba(92,225,230,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CampaignIcon sx={{ color: '#5ce1e6', fontSize: 18 }} />
                  </Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#5ce1e6' }}>Parent Notification</Typography>
                </Box>
                <Stack spacing={1.5}>
                  {[
                    { label: 'Choose a template', desc: 'Select from pre-written messages matched to the incident type — from reassurance to serious concern.' },
                    { label: 'Review and personalize', desc: 'The director previews the message before sending. Every word is deliberate.' },
                    { label: 'Send with confidence', desc: 'Parent receives a professional, clear communication. No panic. No ambiguity.' },
                    { label: 'Automatically logged', desc: 'The sent message becomes part of the permanent incident record — timestamped and auditable.' },
                  ].map(({ label, desc }, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'rgba(92,225,230,0.1)', border: '1px solid rgba(92,225,230,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 10, color: '#5ce1e6' }}>{i + 1}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#f0f0f8', mb: 0.25 }}>{label}</Typography>
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.55 }}>{desc}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Parent photos + quotes */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3 }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>
            Parent Voices
          </Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 6 }}>
            What parents say about safety-first facilities
          </Typography>
          <Grid container spacing={3}>
            {parentPhotos.map(({ photo, quote, attribution }) => (
              <Grid key={attribution} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Box
                      component="img"
                      src={photo}
                      alt={attribution}
                      sx={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(92,225,230,0.25)', flexShrink: 0 }}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.style.display = 'none'; }}
                    />
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', letterSpacing: '0.04em' }}>
                      {attribution}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontStyle: 'italic', fontSize: 14, color: '#d0d0e0', lineHeight: 1.7, flex: 1 }}>
                    "{quote}"
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(92,225,230,0.1)', py: { xs: 8, md: 12 }, px: 3, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>
            Show parents you're the safest choice.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 17, color: '#9499b0', lineHeight: 1.7, mb: 5 }}>
            50% of parents now rank safety above location. Be the facility they're already looking for.
          </Typography>
          <Button variant="contained" size="large" onClick={scrollToContact} sx={{ px: 5, py: 1.75, fontWeight: 700, fontSize: 17, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 32px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo — Become the Safest Choice →
          </Button>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
